import { Extension } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import {
  Dispatch,
  ReactNode,
  SetStateAction,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedoModal } from "./arbiter-components/modal/redo-modal";
import { RedoHorizontalTabs } from "./arbiter-components/tabs/redo-horizontal-tabs";
import { CodeEditorInputField } from "./code-editor";
import { ExtensionArtifactTestPane } from "./extension-artifact-test-pane";
import { Flex } from "./flex";
import { PanelResizer } from "./panel-resizer";
import { Text } from "./text";

/**
 * Decouples the editor from any specific RPC client so it can be embedded by
 * both the merchant app and standalone editors (e.g. the shipping rate-table
 * modal in redo/web) without dragging an RPC dependency into redo/web. The
 * merchant RPC client satisfies this interface structurally.
 */
export interface ExtensionArtifactClient {
  getExtensionArtifact: (input: {
    artifactId: string;
  }) => Promise<{
    artifact: {
      contractId: string;
      inputSelector: string[];
      code: string;
    } | null;
  }>;
  uploadExtensionArtifact: (input: {
    contractId: string;
    code: string;
    inputSelector: string[];
  }) => Promise<{ artifactId: string }>;
  getExtensionContractTypes: (input: {
    contractId: string;
    inputSelector: string[];
  }) => Promise<{
    dts: string;
    universeDts: string;
    topLevelKinds: string[];
    sampleInput?: unknown;
    inputJsonSchema?: unknown;
  }>;
  testExtensionArtifact: (input: {
    contractId: string;
    code: string;
    inputSelector: string[];
    input: unknown;
  }) => Promise<{
    ok: boolean;
    logs: string;
    output?: unknown;
    error?: { stage: string; message: string };
  }>;
}

/** Editor state handed to the optional "Agent Coder" pane. */
export interface ExtensionArtifactAgentCoderPaneContext {
  contractId: string;
  inputSelector: string[];
  code: string;
  setCode: (code: string) => void;
}

export interface ExtensionArtifactTestInputLoader {
  label: string;
  placeholder: string;
  load: (value: string) => Promise<unknown>;
}

/** The loaded contract types + editor state handed to the right pane. */
export interface ExtensionArtifactRightPaneContext {
  /** Generated `Input` type narrowed by the current input selector. */
  dts: string;
  /** Full contract schema, ignoring the input selector. */
  universeDts: string;
  /** All top-level kinds the contract can expose. */
  topLevelKinds: string[];
  contractId: string;
  setContractId: (contractId: string) => void;
  inputSelector: string[];
  setInputSelector: Dispatch<SetStateAction<string[]>>;
  isLoadingArtifact: boolean;
}

export interface ExtensionArtifactCodeEditorModalProps {
  /** Defaults to true. Pass false to keep the editor mounted but hidden. */
  isOpen?: boolean;
  client: ExtensionArtifactClient;
  /** Existing artifact to rehydrate from, or undefined for a fresh artifact. */
  artifactId: string | undefined;
  contractId: string;
  /**
   * Initial input selector. Defaults to `[]`. Contracts that take no
   * merchant-pickable selector should pass the full set of top-level kinds so
   * the input universe isn't pruned.
   */
  defaultInputSelector?: string[];
  /** Shown for a fresh artifact (no artifactId). */
  starterCode: string;
  title: string;
  subtitle: string;
  readonly?: boolean;
  onClose: () => void;
  onSaved: (artifactId: string) => void;
  /** CodeMirror extensions for the editable code pane (e.g. entity chips). */
  codeEditorExtensions?: Extension[];
  /**
   * Remounts the code editor when it changes — needed when async editor
   * extensions become ready after the initial mount.
   */
  editorReloadKey?: string;
  onEditorView?: (view: EditorView | null) => void;
  /** Rendered on the trailing side of the "Code" header (e.g. a toolbar). */
  codeHeaderActions?: ReactNode;
  /**
   * Extra nodes rendered in the left pane after the editor — typically
   * anchored dropdown portals that have no visual flow of their own.
   */
  leftPaneExtras?: ReactNode;
  /** Renders the right configuration / schema pane. */
  renderRightPane: (context: ExtensionArtifactRightPaneContext) => ReactNode;
  /**
   * Renders the "Agent Coder" chat pane; the tab only appears when provided.
   * Supplied by the host app (the chat composes app-level agent UI that
   * redo/web can't depend on).
   */
  renderAgentCoderPane?: (
    context: ExtensionArtifactAgentCoderPaneContext,
  ) => ReactNode;
  /** Loads a real host-domain object into the Test tab (for example, by order number). */
  testInputLoader?: ExtensionArtifactTestInputLoader;
}

const EMPTY_SELECTOR: string[] = [];

/** Minimum width, in px, that either pane may be resized to. */
const MIN_PANE_WIDTH_PX = 240;

/**
 * Shared editor for sandboxed extension artifacts: the merchant authors the
 * code that runs against a contract's input at runtime. Owns the artifact
 * load/save lifecycle and the resizable split layout; consumers supply the
 * right pane (contract/selector/schema) and any code-pane toolbar.
 */
export const ExtensionArtifactCodeEditorModal = memo(
  function ExtensionArtifactCodeEditorModal({
    isOpen = true,
    client,
    artifactId,
    contractId: defaultContractId,
    defaultInputSelector = EMPTY_SELECTOR,
    starterCode,
    title,
    subtitle,
    readonly,
    onClose,
    onSaved,
    codeEditorExtensions,
    editorReloadKey,
    onEditorView,
    codeHeaderActions,
    leftPaneExtras,
    renderRightPane,
    renderAgentCoderPane,
    testInputLoader,
  }: ExtensionArtifactCodeEditorModalProps) {
    // Start empty when rehydrating an existing artifact so the editor never
    // flashes the starter code before the real code arrives.
    const [code, setCode] = useState<string>(artifactId ? "" : starterCode);
    const [inputSelector, setInputSelector] =
      useState<string[]>(defaultInputSelector);
    const [contractId, setContractId] = useState<string>(defaultContractId);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [loadedArtifactId, setLoadedArtifactId] = useState<string | null>(
      null,
    );

    // Rehydrate from the stored artifact when an artifactId is present. Only
    // fires once per artifactId so local edits aren't clobbered.
    const storedArtifact = useLoad(
      async (_signal) => {
        if (!artifactId) {
          return null;
        }
        try {
          const { artifact } = await client.getExtensionArtifact({
            artifactId,
          });
          return artifact;
        } catch {
          return null;
        }
      },
      [artifactId, client],
    );

    useEffect(() => {
      const a = storedArtifact?.value;
      if (!a) {
        return;
      }
      if (loadedArtifactId === artifactId) {
        return;
      }
      setCode(a.code);
      setInputSelector(a.inputSelector);
      setContractId(a.contractId);
      setLoadedArtifactId(artifactId ?? null);
    }, [storedArtifact?.value, artifactId, loadedArtifactId]);

    const contractTypes = useLoad(
      async (_signal) => {
        try {
          return await client.getExtensionContractTypes({
            contractId,
            inputSelector,
          });
        } catch {
          return {
            dts: "",
            universeDts: "",
            topLevelKinds: [],
            sampleInput: undefined,
            inputJsonSchema: undefined,
          };
        }
      },
      [client, contractId, inputSelector],
    );

    const handleSave = useHandler(async () => {
      setUploadError(null);
      setUploading(true);
      try {
        const { artifactId: newArtifactId } =
          await client.uploadExtensionArtifact({
            contractId,
            code,
            inputSelector,
          });
        setLoadedArtifactId(newArtifactId);
        onSaved(newArtifactId);
      } catch (err) {
        setUploadError(err instanceof Error ? err.message : String(err));
      } finally {
        setUploading(false);
      }
    });

    const splitContainerRef = useRef<HTMLDivElement | null>(null);
    // Stored as a fraction of the container so the split stays proportional
    // across modal/viewport sizes; the left pane starts as the majority.
    const [leftFraction, setLeftFraction] = useState<number>(0.6);
    const handlePanelResize = useHandler((delta: number) => {
      const total = splitContainerRef.current?.offsetWidth ?? 0;
      if (!total) {
        return;
      }
      const minFraction = MIN_PANE_WIDTH_PX / total;
      setLeftFraction((fraction) =>
        Math.max(
          minFraction,
          Math.min(1 - minFraction, fraction + delta / total),
        ),
      );
    });

    const isLoadingArtifact =
      Boolean(artifactId) && loadedArtifactId !== artifactId;
    const dts = contractTypes?.value?.dts ?? "";
    const universeDts = contractTypes?.value?.universeDts ?? "";
    const topLevelKinds = contractTypes?.value?.topLevelKinds ?? [];
    const sampleInput = contractTypes?.value?.sampleInput;
    const inputJsonSchema = contractTypes?.value?.inputJsonSchema;

    const [rightPaneTab, setRightPaneTab] = useState<
      "types" | "test" | "agentCoder"
    >("types");

    return (
      <RedoModal
        isOpen={isOpen}
        modalSize="near-full"
        noPadding
        onModalCloseRequested={onClose}
        primaryButton={{
          text: uploading ? "Verifying…" : "Save",
          onClick: handleSave,
          disabled: readonly || uploading || isLoadingArtifact,
        }}
        secondaryButton={{ text: "Cancel", onClick: onClose }}
        subtitle={subtitle}
        title={title}
      >
        <Flex
          borderBottomWidth="1px"
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="row"
          gap="none"
          overflow="hidden"
          ref={splitContainerRef}
          style={{ boxSizing: "border-box", height: "100%" }}
        >
          <Flex
            dir="column"
            gap="lg"
            p="lg"
            shrink={0}
            style={{
              width: `${leftFraction * 100}%`,
              minWidth: 0,
              minHeight: 0,
            }}
          >
            <Flex align="center" gap="sm" justify="space-between" shrink={0}>
              <Flex align="center" gap="sm">
                <Text fontSize="xs" fontWeight="medium">
                  Code
                </Text>
                {isLoadingArtifact ? (
                  <Text fontSize="xs" textColor="secondary">
                    loading…
                  </Text>
                ) : null}
              </Flex>
              {codeHeaderActions}
            </Flex>
            <Flex dir="column" grow={1} style={{ minHeight: 0 }}>
              <CodeEditorInputField
                extensions={codeEditorExtensions}
                key={`${isLoadingArtifact ? "loading" : "ready"}:${
                  editorReloadKey ?? ""
                }`}
                language="javascript"
                onEditorView={onEditorView}
                readonly={readonly || isLoadingArtifact}
                setValue={setCode}
                value={code}
              />
            </Flex>
            {leftPaneExtras}
            {uploadError ? (
              <Text fontSize="xs" textColor="error">
                {uploadError}
              </Text>
            ) : null}
          </Flex>

          <PanelResizer onResize={handlePanelResize} />

          <Flex
            bgColor="tertiary"
            borderColor="secondary"
            borderLeftWidth="1px"
            borderStyle="solid"
            dir="column"
            gap="none"
            grow={1}
            style={{ minWidth: 0, minHeight: 0 }}
          >
            <Flex pt="md" shrink={0}>
              <RedoHorizontalTabs<"types" | "test" | "agentCoder">
                pl="lg"
                selectedTab={rightPaneTab}
                setSelectedTab={setRightPaneTab}
                size="xs"
                tabs={[
                  { key: "types", label: "Types" },
                  { key: "test", label: "Test" },
                  ...(renderAgentCoderPane
                    ? [{ key: "agentCoder" as const, label: "Agent Coder" }]
                    : []),
                ]}
              />
            </Flex>
            {/* Both tabs stay mounted so edited test inputs and run results
                survive switching back to Types. */}
            <Flex
              dir="column"
              gap="lg"
              grow={1}
              overflowY="auto"
              p="lg"
              style={{
                minHeight: 0,
                display: rightPaneTab === "types" ? undefined : "none",
              }}
            >
              {renderRightPane({
                dts,
                universeDts,
                topLevelKinds,
                contractId,
                setContractId,
                inputSelector,
                setInputSelector,
                isLoadingArtifact,
              })}
            </Flex>
            <Flex
              dir="column"
              gap="lg"
              grow={1}
              overflowY="auto"
              p="lg"
              style={{
                minHeight: 0,
                display: rightPaneTab === "test" ? undefined : "none",
              }}
            >
              <ExtensionArtifactTestPane
                client={client}
                code={code}
                contractId={contractId}
                inputJsonSchema={inputJsonSchema}
                inputSelector={inputSelector}
                sampleInput={sampleInput}
                testInputLoader={testInputLoader}
              />
            </Flex>
            {renderAgentCoderPane ? (
              <Flex
                dir="column"
                gap="lg"
                grow={1}
                p="lg"
                style={{
                  minHeight: 0,
                  display: rightPaneTab === "agentCoder" ? undefined : "none",
                }}
              >
                {renderAgentCoderPane({
                  contractId,
                  inputSelector,
                  code,
                  setCode,
                })}
              </Flex>
            ) : null}
          </Flex>
        </Flex>
      </RedoModal>
    );
  },
);
